<script setup lang="ts">
import { type IValidationResult } from "@/interfaces";
import CheckboxComponent from "@/stories/form/Checkbox/CheckboxComponent.vue";
import { toRefs, ref } from "vue";
import InputValidationError from "@/stories/form/InputValidationError/InputValidationError.vue";

interface Props {
  name: string;
  validation?: IValidationResult | null;
  compactText?: string;
  text: string;
  modelValue?: boolean;
  error?: string;
  disabled?: boolean;
  isRequired?: boolean;
  isStacked?: boolean;
}

const props = defineProps<Props>();

const { modelValue, disabled, validation, name, isStacked } = toRefs(props);

const emit = defineEmits(["update:modelValue"]);

const seeFullText = ref(false);

const handleTextClick = (event: Event) => {
  if (disabled.value !== true) {
    event.preventDefault();
    handleInput(!modelValue.value);
  }
};

const handleInput = (value: boolean) => {
  emit("update:modelValue", value);

  if (validation.value && validation.value.errors) {
    delete validation.value.errors[name.value];
  }
};
</script>

<template>
  <div>
    <div
      role="button"
      class="inline-flex justify-between gap-1 items-center focus-visible-strong rounded p-[2px] -m-[2px]"
      :class="{ 'flex-col gap-0': isStacked }"
      :tabindex="disabled ? -1 : 0"
      @keydown.enter="handleTextClick"
      @click="handleTextClick"
    >
      <CheckboxComponent
        :checked="modelValue"
        :disabled="disabled"
        :tabindex="-1"
      />
      <div class="flex-grow">
        <button
          class="m-0 block text-left focusVisible rounded-lg b300"
          :class="{
            'cursor-pointer text-neutral1000': !disabled,
            'text-neutral600': disabled,
          }"
          :disabled="disabled"
          :tabindex="-1"
        >
          <div v-if="compactText">
            <div v-if="seeFullText">
              {{ text }} <span v-if="isRequired">*</span>
            </div>
            <div v-else>
              {{ compactText }}
              <span v-if="isRequired">*</span>
            </div>
          </div>
          <div v-else>{{ text }} <span v-if="isRequired">*</span></div>
        </button>
        <div v-if="compactText" class="mt-2">
          <button
            type="button"
            class="test-read-more-button"
            :class="
              [
                'h300',
                disabled ? 'text-neutral600' : 'text-neutral1000',
                'focusVisible rounded-lg',
              ].join(' ')
            "
            :disabled="disabled"
            @click="seeFullText = !seeFullText"
          >
            {{ seeFullText ? $t("global.readLess") : $t("global.readMore") }}
          </button>
        </div>
      </div>
    </div>
    <InputValidationError
      :name="name"
      :validation="validation"
      :error-message-key="error"
    />
  </div>
</template>

<style lang="scss"></style>
